// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import catalogStyles from '../../bank-upload/styles/institution-catalog.module.css';
import formStyles from '../../bank-upload/styles/bank-upload-form.module.css';
import { LabelWithInfoTooltip } from '@client/banks/components/shared/label-with-info-tooltip';
import { LinkedLiquiditySection } from '@client/banks/components/shared/linked-liquidity-section';
import { DateInput } from '@shared/ui-kit/components/date-input';
import { Input, Label } from '@shared/ui-kit/components/input';
import { OptionsSelector } from '@shared/ui-kit/components/options-selector';
import { Translation } from '@shared/ui-kit/providers/translation-provider/translation';
import { DECIMAL_SEPARATORS } from '@shared/ui-kit/utils/constants';
import { defineComponent } from '@shared/ui-kit/utils/define-component';
import { Typography } from '@mui/material';
import { Controller } from 'react-hook-form';
import { NumericFormat } from 'react-number-format';
import { PositionAssetSearchField } from './position-asset-search-field';
import { usePositionForm } from './use-position-form';

export const PositionForm = defineComponent({
  displayName: 'PositionForm',
  useSetup: usePositionForm,
  View: ({
    formId,
    onSubmit,
    currencyOptions,
    accountIbanOptions,
    defaultCurrency,
    linkedLiquidityApiRef,
    onLinkedLiquidityGateChange,
    control,
    handleSubmit,
    t,
    currencyPrefix,
    assetTypeOptions,
    todayMaxDate,
    marketValue,
    onAssetSelect,
    onIsinChange,
    isin,
  }) => (
    <form id={formId} onSubmit={handleSubmit(onSubmit)}>
      <fieldset className="m-0 flex min-w-0 flex-col gap-7 border-0 p-0">
        <LinkedLiquiditySection
          currencyOptions={currencyOptions}
          accountIbanOptions={accountIbanOptions}
          defaultCurrency={defaultCurrency}
          apiRef={linkedLiquidityApiRef}
          onGateChange={onLinkedLiquidityGateChange}
          labelI18nKey="aggregator.create-bank.positions.account"
        />

        <section className={formStyles['form-section']}>
          <Typography
            component="h3"
            variant="subtitle1"
            className={formStyles['section-title']}
          >
            <Translation
              i18nKey="aggregator.create-bank.positions.section.details"
              ns="aggregator"
            />
          </Typography>

          <div className={formStyles['field-grid']}>
            <Controller
              control={control}
              name="isin"
              render={({ fieldState }) => (
                <PositionAssetSearchField
                  isin={isin}
                  error={!!fieldState.error}
                  onIsinChange={onIsinChange}
                  onAssetSelect={onAssetSelect}
                />
              )}
            />

            <Controller
              control={control}
              name="name"
              render={({ field, fieldState }) => (
                <div className={catalogStyles['field-stack']}>
                  <Label
                    htmlFor="bank-upload-position-name"
                    required
                    error={!!fieldState.error}
                  >
                    <Translation
                      i18nKey="aggregator.create-bank.positions.name"
                      ns="aggregator"
                    />
                  </Label>
                  <Input
                    id="bank-upload-position-name"
                    {...field}
                    value={field.value ?? ''}
                    error={!!fieldState.error}
                    placeholder={t(
                      'aggregator.create-bank.positions.name-placeholder',
                      { ns: 'aggregator' },
                    )}
                    data-testid="bank-upload-position-name"
                  />
                </div>
              )}
            />

            <Controller
              control={control}
              name="assetType"
              render={({ field, fieldState }) => (
                <div className={catalogStyles['field-stack']}>
                  <Label required error={!!fieldState.error}>
                    <Translation
                      i18nKey="aggregator.create-bank.positions.asset-type"
                      ns="aggregator"
                    />
                  </Label>
                  <OptionsSelector
                    optionsAvailable={[...assetTypeOptions]}
                    optionSelected={field.value ?? ''}
                    onChangeValueSelected={field.onChange}
                    defaultText={t('aggregator.create-bank.positions.asset-type', {
                      ns: 'aggregator',
                    })}
                    selectType="white"
                    data-testid="bank-upload-position-asset-type"
                  />
                </div>
              )}
            />

            <Controller
              control={control}
              name="currency"
              render={({ field, fieldState }) => (
                <div className={catalogStyles['field-stack']}>
                  <Label required error={!!fieldState.error}>
                    <Translation
                      i18nKey="aggregator.create-bank.accounts.currency"
                      ns="aggregator"
                    />
                  </Label>
                  <OptionsSelector
                    optionsAvailable={[...currencyOptions]}
                    optionSelected={field.value ?? ''}
                    onChangeValueSelected={field.onChange}
                    defaultText={t('aggregator.create-bank.accounts.currency', {
                      ns: 'aggregator',
                    })}
                    selectType="white"
                    data-testid="bank-upload-position-currency"
                  />
                </div>
              )}
            />
          </div>
        </section>

        <section className={formStyles['form-section']}>
          <Typography
            component="h3"
            variant="subtitle1"
            className={formStyles['section-title']}
          >
            <Translation
              i18nKey="aggregator.create-bank.positions.section.valuation"
              ns="aggregator"
            />
          </Typography>

          <div className={formStyles['field-grid']}>
            <Controller
              control={control}
              name="acquisitionCost"
              render={({ field, fieldState }) => (
                <div className={catalogStyles['field-stack']}>
                  <Label
                    htmlFor="bank-upload-position-acquisition-cost"
                    error={!!fieldState.error}
                  >
                    <Translation
                      i18nKey="aggregator.create-bank.positions.acquisition-cost"
                      ns="aggregator"
                    />
                  </Label>
                  <NumericFormat
                    customInput={Input}
                    id="bank-upload-position-acquisition-cost"
                    value={field.value ?? ''}
                    onValueChange={({ floatValue }) =>
                      field.onChange(floatValue ?? undefined)
                    }
                    onBlur={field.onBlur}
                    name={field.name}
                    inputMode="decimal"
                    allowNegative={false}
                    decimalScale={2}
                    thousandSeparator="."
                    decimalSeparator=","
                    allowedDecimalSeparators={DECIMAL_SEPARATORS}
                    prefix={currencyPrefix ? `${currencyPrefix} ` : undefined}
                    error={!!fieldState.error}
                    placeholder={t(
                      'aggregator.create-bank.positions.acquisition-cost-placeholder',
                      { ns: 'aggregator' },
                    )}
                    data-testid="bank-upload-position-acquisition-cost"
                  />
                </div>
              )}
            />

            <Controller
              control={control}
              name="quantity"
              render={({ field, fieldState }) => (
                <div className={catalogStyles['field-stack']}>
                  <Label
                    htmlFor="bank-upload-position-quantity"
                    required
                    error={!!fieldState.error}
                  >
                    <Translation
                      i18nKey="aggregator.create-bank.positions.quantity"
                      ns="aggregator"
                    />
                  </Label>
                  <NumericFormat
                    customInput={Input}
                    id="bank-upload-position-quantity"
                    value={field.value || ''}
                    onValueChange={({ floatValue }) =>
                      field.onChange(floatValue ?? 0)
                    }
                    onBlur={field.onBlur}
                    name={field.name}
                    inputMode="decimal"
                    allowNegative={false}
                    decimalScale={4}
                    thousandSeparator="."
                    decimalSeparator=","
                    allowedDecimalSeparators={DECIMAL_SEPARATORS}
                    error={!!fieldState.error}
                    placeholder={t(
                      'aggregator.create-bank.positions.quantity-placeholder',
                      { ns: 'aggregator' },
                    )}
                    data-testid="bank-upload-position-quantity"
                  />
                </div>
              )}
            />

            <Controller
              control={control}
              name="price"
              render={({ field, fieldState }) => (
                <div className={catalogStyles['field-stack']}>
                  <Label
                    htmlFor="bank-upload-position-price"
                    required
                    error={!!fieldState.error}
                  >
                    <Translation
                      i18nKey="aggregator.create-bank.positions.price"
                      ns="aggregator"
                    />
                  </Label>
                  <NumericFormat
                    customInput={Input}
                    id="bank-upload-position-price"
                    value={field.value || ''}
                    onValueChange={({ floatValue }) =>
                      field.onChange(floatValue ?? 0)
                    }
                    onBlur={field.onBlur}
                    name={field.name}
                    inputMode="decimal"
                    allowNegative={false}
                    decimalScale={2}
                    thousandSeparator="."
                    decimalSeparator=","
                    allowedDecimalSeparators={DECIMAL_SEPARATORS}
                    prefix={currencyPrefix ? `${currencyPrefix} ` : undefined}
                    error={!!fieldState.error}
                    placeholder={t(
                      'aggregator.create-bank.positions.price-placeholder',
                      { ns: 'aggregator' },
                    )}
                    data-testid="bank-upload-position-price"
                  />
                </div>
              )}
            />

            <div className={catalogStyles['field-stack']}>
              <LabelWithInfoTooltip
                htmlFor="bank-upload-position-market-value"
                tooltipI18nKey="aggregator.create-bank.positions.market-value-tooltip"
              >
                <Translation
                  i18nKey="aggregator.create-bank.positions.market-value"
                  ns="aggregator"
                />
              </LabelWithInfoTooltip>
              <NumericFormat
                customInput={Input}
                id="bank-upload-position-market-value"
                value={marketValue}
                inputMode="decimal"
                allowNegative={false}
                decimalScale={2}
                thousandSeparator="."
                decimalSeparator=","
                allowedDecimalSeparators={DECIMAL_SEPARATORS}
                prefix={currencyPrefix ? `${currencyPrefix} ` : undefined}
                readOnly
                data-testid="bank-upload-position-market-value"
              />
            </div>

            <Controller
              control={control}
              name="snapshotDate"
              render={({ field, fieldState }) => (
                <div className={catalogStyles['field-stack']}>
                  <Label
                    htmlFor="bank-upload-position-snapshot-date"
                    required
                    error={!!fieldState.error}
                  >
                    <Translation
                      i18nKey="aggregator.create-bank.positions.valuation-date"
                      ns="aggregator"
                    />
                  </Label>
                  <DateInput
                    id="bank-upload-position-snapshot-date"
                    value={field.value}
                    onChange={field.onChange}
                    maxDate={todayMaxDate}
                    error={!!fieldState.error}
                    data-testid="bank-upload-position-snapshot-date"
                  />
                </div>
              )}
            />
          </div>
        </section>
      </fieldset>
    </form>
  ),
});
